import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { SIGNUP_PATHS, UPLOAD_DOCS_ERROR } from '../../../../constants';
import { useUserToken } from '../../../../hooks/userTokenHook';
import AppCheckbox from '../../../../modules/app-checkbox/app-checkbox';
import FileLoader from '../../../../modules/file-loader/file-loader';
import IconBtn from '../../../../modules/icon-btn/icon-btn';
import { useAppDispatch } from '../../../../store';
import { setPush } from '../../../../store/actions/app';
import {
  checkUploadedDocs,
  uploadAgreement,
  uploadProfileDoc,
} from '../../../../store/actions/signup';
import { RootState, SignupDocsType } from '../../../../types';
import { COMPANY_UPLOAD_DOC_TYPE } from '../../../../types';
import FormFooter from '../form-footer/form-footer';
import { UiSelect } from '@ru-digital-assets/ui-component';
import { useSign } from 'hooks';

import './docs-step.scss';

const THREE_MB = 3e6;

enum DocsLabels {
  AUTHORITY_CONFIRMATION = 'Документ, подтверждающий полномочия ',
  PASSPORT = 'Документ, удостоверяющий личность',
  TIN = 'Свидетельство ИНН компании',
  TIN_PERSONAL = 'Свидетельство ИНН физического лица',
  PERSONAL_DATA_AGREEMENT = 'Согласие на обработку персональных данных',
}

interface DocsStepProps {
  docs: SignupDocsType;
}

const DocsStep: React.FC<DocsStepProps> = () => {
  const [timestamp, setTimestamp] = useState(Date.now());
  const step = useSelector((s: RootState) => s.signup.step);
  const { token } = useUserToken();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [isConfirmed, setIsConfirmed] = useState(false);
  // const [isRisk, setIsRisk] = useState(false);
  const [isDisabledBtn, setIsDisabledBtn] = useState(false);
  const docs = useSelector((s: RootState) => s.signup.docs);
  const isPersonal = useSelector((s: RootState) => s.signup.isPersonal);

  const {
    certificatesList,
    selectedCertificate,
    onUserSelectCertificate,
  } = useSign();

  useEffect(() => {
    dispatch(checkUploadedDocs());
    setTimestamp(Date.now());

    if (!token) history.push(SIGNUP_PATHS.CODE);

    if (step && step !== 'ORGANIZATION_DATA_UPLOADING') {
      history.push(SIGNUP_PATHS.NEW);
    }
  }, []);

  const uploadFile = async (file: File, type: COMPANY_UPLOAD_DOC_TYPE) => {
    if (file.size <= THREE_MB) {
      if (file.type === 'application/pdf') {
        dispatch(
          uploadProfileDoc({
            doc: file,
            type,
          })
        );
      } else {
        dispatch(setPush({ message: UPLOAD_DOCS_ERROR.FORMAT, type: 'error' }));
      }
    } else {
      dispatch(setPush({ message: UPLOAD_DOCS_ERROR.SIZE, type: 'error' }));
    }
  };

  const onSubmit = useCallback(() => {
    setIsDisabledBtn(true);
    dispatch(uploadAgreement()).then((action) => {
      if (action.meta.requestStatus === 'fulfilled') {
        history.push(SIGNUP_PATHS.AGREEMENT);
      }
    });
  }, [uploadAgreement]);

  const checkConfirmed = useMemo(
    () =>
      !!(
        docs &&
        docs.PERSONAL_DATA_AGREEMENT &&
        (docs.AUTHORITY_CONFIRMATION || isPersonal) &&
        docs.TIN &&
        docs.PASSPORT &&
        isConfirmed
      ),
    [docs, isConfirmed]
  );

  return (
    <form className="login-form login-form--4 docs-step">
      <p className="p p_font-size_32 p_font-weight_light mb_28">Регистрация</p>
      <p className="p p_font-size_18 p_bold mb_4">
        Загрузите и подпишите документы:
      </p>
      <p className="p mb_24">
        Размер одного документа не должен превышать 5 мб,
        <br />
        допустимый формат файлов – .pdf
      </p>
      <div className="login-form__body">
        <div className="login-form__container-545">
          {!isPersonal && (
            <div className="login-form__item-input--sm">
              <FileLoader
                key={DocsLabels.AUTHORITY_CONFIRMATION + timestamp}
                fileNameProp={docs?.AUTHORITY_CONFIRMATION}
                onFileReady={(file) =>
                  uploadFile(file, 'AUTHORITY_CONFIRMATION')
                }
                label={DocsLabels.AUTHORITY_CONFIRMATION}
              />
            </div>
          )}
          <div className="login-form__item-input--sm">
            <FileLoader
              key={DocsLabels.PASSPORT + timestamp}
              fileNameProp={docs?.PASSPORT}
              onFileReady={(file) => uploadFile(file, 'PASSPORT')}
              label={DocsLabels.PASSPORT}
            />
          </div>
          <div className="login-form__item-input--sm">
            <FileLoader
              key={DocsLabels.TIN + timestamp}
              fileNameProp={docs?.TIN}
              onFileReady={(file) => uploadFile(file, 'TIN')}
              label={isPersonal ? DocsLabels.TIN_PERSONAL : DocsLabels.TIN}
            />
          </div>
          <div className="login-form__item-input--sm">
            <FileLoader
              key={DocsLabels.PERSONAL_DATA_AGREEMENT + timestamp}
              fileNameProp={docs?.PERSONAL_DATA_AGREEMENT}
              onFileReady={(file) =>
                uploadFile(file, 'PERSONAL_DATA_AGREEMENT')
              }
              label={DocsLabels.PERSONAL_DATA_AGREEMENT}
            />
          </div>
        </div>
        <div className="mb_24 mt_32">
          <AppCheckbox
            sm
            checked={isConfirmed}
            onChangeValue={(value) => {
              setIsConfirmed(value);
            }}
            title="Заверяю подлинность прилагаемых документов"
          />
        </div>
        {/*<div>*/}
        {/*  <AppCheckbox*/}
        {/*    sm*/}
        {/*    checked={isRisk}*/}
        {/*    onChangeValue={(value) => {*/}
        {/*      setIsRisk(value);*/}
        {/*    }}*/}
        {/*    titleComponent={*/}
        {/*      <>*/}
        {/*        Подтверждаю ознакомление с{' '}*/}
        {/*        <a*/}
        {/*          href="https://dev.gp7.digital/documents/risk_declaration.pdf"*/}
        {/*          target="_blank"*/}
        {/*          rel="noreferrer"*/}
        {/*        >*/}
        {/*          декларацией о рисках*/}
        {/*        </a>*/}
        {/*      </>*/}
        {/*    }*/}
        {/*  />*/}
        {/*</div>*/}
      </div>
      <FormFooter
        actions={
          <div style={{ width: '430px' }}>
            <div className="mb_32">
              <UiSelect
                placeholder="Выберите подпись"
                value={selectedCertificate}
                options={certificatesList}
                lg
                onChange={onUserSelectCertificate}
                disabled={certificatesList.length === 0}
              />
            </div>
            <IconBtn
              disabled={
                !selectedCertificate || !checkConfirmed || isDisabledBtn
              }
              onClick={onSubmit}
              title="Подписать документы"
              lg
            />
          </div>
        }
      />
    </form>
  );
};

export default DocsStep;
